import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import StatusBadge from "../components/StatusBadge";
import LiveEmptyState from "../components/LiveEmptyState";
import useLiveMode from "../hooks/useLiveMode";
import api from "../lib/api";
import { formatDate } from "../utils/format";
import { toast } from "sonner";

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState(searchParams.get("severity") || "all");
  const { isLive, isConnected } = useLiveMode();

  const load = () => api.get("/alerts").then((r) => setAlerts(r.data || [])).catch(() => setAlerts([]));
  useEffect(() => { load(); }, []);
  useEffect(() => { setFilter(searchParams.get("severity") || "all"); }, [searchParams]);

  const setStatus = async (id, status) => {
    await api.patch(`/alerts/${id}`, { status });
    toast.success(`Alert marked ${status}`);
    load();
  };

  const visible = alerts.filter((a) => filter === "all" || a.severity === filter);
  const noRows = alerts.length === 0;
  const showConnectorCta = isLive && noRows && !isConnected;
  const showModuleEmpty = isLive && noRows && isConnected;

  return (
    <Layout title="Alerts & Monitoring" subtitle={`${visible.length} alerts`}>
      {showConnectorCta ? (
        <LiveEmptyState entity="alerts" />
      ) : showModuleEmpty ? (
        <LiveEmptyState
          entity="alerts"
          variant="module"
          title="No alerts generated yet"
          message="Alerts are auto-generated as your live campaigns hit thresholds (zero-spend streaks, CPL anomalies, budget caps). Once your sync produces enough data, alerts will appear here automatically."
        />
      ) : (
      <>
      <div className="flex gap-1 mb-4 border border-border rounded-sm p-1 bg-card inline-flex">
        {["all", "critical", "warning", "info"].map((s) => (
          <button
            key={s}
            data-testid={`alert-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`px-4 py-1.5 text-xs uppercase tracking-wider font-semibold rounded-sm transition-colors ${
              filter === s ? (s === "critical" ? "bg-[hsl(var(--critical))] text-white" : s === "warning" ? "bg-[hsl(var(--warning))] text-[hsl(220_20%_15%)]" : "bg-primary text-primary-foreground") : "text-muted-foreground hover:bg-secondary"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="bg-card border border-border rounded-sm" data-testid="alerts-list">
        {visible.map((a) => (
          <div key={a.id} className="flex items-start gap-4 px-5 py-4 border-b border-border last:border-b-0">
            <StatusBadge tone={a.severity === "critical" ? "critical" : a.severity === "warning" ? "warning" : "primary"} solid={a.severity === "critical"}>
              {a.severity}
            </StatusBadge>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm">{a.title}</div>
              <div className="text-xs text-muted-foreground mt-1">{a.description}</div>
              {a.suggested_action && (
                <div className="mt-2 text-xs"><span className="label-mono mr-2">Suggested</span>{a.suggested_action}</div>
              )}
              <div className="flex items-center gap-3 mt-2 text-[11px] text-muted-foreground">
                {a.campaign_name && <span>Campaign: {a.campaign_name}</span>}
                {a.client_name && <span>· Client: {a.client_name}</span>}
                <span>· {formatDate(a.created_at)}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge tone="muted">{a.status}</StatusBadge>
              {a.status !== "resolved" && (
                <button
                  onClick={() => setStatus(a.id, "resolved")}
                  data-testid={`resolve-alert-${a.id}`}
                  className="h-8 px-3 text-xs font-medium border border-border hover:bg-secondary rounded-sm"
                >
                  Resolve
                </button>
              )}
            </div>
          </div>
        ))}
        {visible.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No alerts in this view.</div>}
      </div>
      </>
      )}
    </Layout>
  );
}
